import { useI18n } from "../lib/i18n";

/**
 * EN / ES pill toggle.
 *
 *  - Active language: gold background, dark text.
 *  - Inactive language: dark background, gold outline + gold text.
 *  - Switching is instant — `useI18n().setLang()` updates context, persists to
 *    localStorage, and fires PATCH /users/me when logged in (handled in i18n.js).
 *
 * `size` may be "sm" (compact, 28px tall — used in footer/onboarding pre-auth)
 * or "md" (44px tall — used in Settings/Splash/ProfileMenu) so the toggle
 * always satisfies a 48px touch target on the screens that need it most.
 */
export default function LanguageToggle({ size = "md", className = "", testIdPrefix = "lang" }) {
  const { lang, setLang } = useI18n();
  const dims = size === "sm"
    ? { h: 28, pad: "0 12px", font: 11, gap: 4 }
    : { h: 44, pad: "0 18px", font: 13, gap: 6 };

  const Pill = ({ value, label }) => {
    const active = lang === value;
    return (
      <button
        type="button"
        data-testid={`${testIdPrefix}-${value}`}
        onClick={() => setLang(value)}
        aria-pressed={active}
        aria-label={`Switch language to ${label}`}
        className="transition-colors"
        style={{
          height: dims.h,
          minWidth: dims.h + 18,
          padding: dims.pad,
          borderRadius: 999,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: dims.font,
          fontWeight: 600,
          letterSpacing: "0.12em",
          background: active ? "#E8B84B" : "transparent",
          color: active ? "#0B0B0B" : "#E8B84B",
          border: active ? "1px solid #E8B84B" : "1px solid rgba(232,184,75,0.5)",
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      data-testid={`${testIdPrefix}-toggle`}
      className={`inline-flex items-center ${className}`}
      style={{ gap: dims.gap }}
      role="group"
      aria-label="Language selector"
    >
      <Pill value="en" label="EN" />
      <Pill value="es" label="ES" />
    </div>
  );
}
